import React, { FC, useState, useEffect } from 'react'
import { Modal, Button, Form, Spinner } from 'react-bootstrap'
import { useLazyQuery } from '@apollo/client'
import { GET_ALL_CONTACTS_API } from '../../../gql/queries/contactsApiQueries'
import { PhoneProviderActions } from '../../../components/telephony/PhoneProviderActions'
import {useIntl} from 'react-intl'
import {CHAT_MESSAGES as C} from '../chatMessages'

// Inicial de respaldo cuando el contacto no tiene nombre — no es texto.
const UNKNOWN_INITIAL = '?'

export type ContactSearchResult = {
    id: string
    fullName: string
    phone: string
    email?: string
}

type Props = {
    show: boolean
    onHide: () => void
    onSelect: (contact: ContactSearchResult) => void
}

// GraphQL response type
interface IContactNode {
    id: string
    fullName: string
    phone: string
    email?: string
}

interface IResContactsAPI {
    allContacts: {
        totalCount: number
        edges: {
            node: IContactNode
        }[]
    }
}

const NewChatModal: FC<Props> = ({ show, onHide, onSelect }) => {
  const intl = useIntl()
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<ContactSearchResult[]>([])
    const [searched, setSearched] = useState(false)

    const [searchContacts, { loading }] = useLazyQuery<IResContactsAPI>(GET_ALL_CONTACTS_API, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            const contacts = data?.allContacts?.edges?.map(edge => ({
                id: edge.node.id,
                fullName: edge.node.fullName,
                phone: edge.node.phone,
                email: edge.node.email
            })) || []
            setResults(contacts)
        },
        onError: () => {
            setResults([])
        }
    })

    const handleSearch = async (e?: React.FormEvent) => {
        e?.preventDefault()
        if (!query.trim()) return

        setSearched(true)
        searchContacts({
            variables: {
                name: query,
                first: 20,
                offset: 0
            }
        })
    }

    // Reset state when modal closes/opens - no auto-load
    useEffect(() => {
        if (show) {
            setQuery('')
            setSearched(false)
            setResults([])
        }
    }, [show])

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>{intl.formatMessage(C.newConversation)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSearch}>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(C.searchContact)}</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text"
                                placeholder={intl.formatMessage(C.newSearchPlaceholder)}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                autoFocus
                            />
                            <Button variant="primary" type="submit" disabled={loading || !query.trim()}>
                                {loading ? <Spinner animation="border" size="sm" /> : <i className="bi bi-search"></i>}
                            </Button>
                        </div>
                    </Form.Group>
                </Form>

                <div className="list-group" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {results.map(contact => (
                        <button
                            key={contact.id}
                            type="button"
                            className="list-group-item list-group-item-action d-flex align-items-center"
                            onClick={() => onSelect(contact)}
                        >
                            <div className='symbol symbol-35px symbol-circle me-3'>
                                <span className='symbol-label bg-light-info text-info fs-6 fw-bolder'>
                                    {contact.fullName?.charAt(0) || UNKNOWN_INITIAL}
                                </span>
                            </div>
                            <div>
                                <div className="fw-bold">{contact.fullName}</div>
                                <div className="text-muted small d-flex flex-wrap align-items-center gap-2">
                                    {contact.phone && (
                                        <PhoneProviderActions
                                            phone={contact.phone}
                                            stopPropagation={false}
                                            className='d-inline-flex flex-column align-items-start'
                                            textClassName='text-muted small'
                                        />
                                    )}
                                    {contact.email && <span>{contact.email}</span>}
                                </div>
                            </div>
                        </button>
                    ))}

                    {!loading && !searched && (
                        <div className="text-center text-muted mt-4 py-3">
                            <i className="bi bi-search fs-1 d-block mb-2"></i>
                            <span>{intl.formatMessage(C.newHint)}</span>
                        </div>
                    )}

                    {!loading && searched && results.length === 0 && (
                        <div className="text-center text-muted mt-3">
                            {intl.formatMessage(C.newNoResults, {query})}
                        </div>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    )
}

export { NewChatModal }
